/**
 * RichText Component - Business Logic Description
 * 
 * This component renders rich text content from Strapi CMS using the BlocksRenderer.
 * It handles various content types including paragraphs, headings, lists, images, links,
 * and special embedded content like iframes and Twitter embeds.
 * 
 * Key features:
 * - Custom link processing for CTA-style links (link-only detection)
 * - iframe and Twitter embed detection and rendering
 * - Custom text box rendering from code blocks with NSC brand color styling:
 *   - <blue-box>: nsc-ice-blue background with nsc-dark-blue text
 *   - <yellow-box>: nsc-yellow background with nsc-dark-blue text  
 *   - <white-box>: white background with nsc-dark-blue text
 * - Anchor points for page navigation using code blocks:
 *   - <anchor id="your-id" />: Creates a scroll target that can be accessed via URL hash (#your-id)
 *   - Automatically scrolls to anchor on page load if hash is present in URL
 * - Responsive text color handling based on background colors
 * - Standard rich text elements (headings, lists, images, links)
 */

"use client";

import Link from "next/link";
import React, { useEffect } from "react";

import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import Arrow from "@/components/icons/Arrow";
import { cn, getTextColorByBgColor } from "@/lib/utils";

// The list marker (number for <ol>, dot for <ul>) is rendered by the CSS
// counter on `.rich-text ol/ul li::before` (see globals.scss). The <li> only
// needs `relative` so that absolutely-positioned marker anchors to it.
function RichTextListItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="relative mt-2 first:mt-0 font-roboto font-light text-lg leading-[30px]">
      {children}
    </li>
  );
}

interface RichTextProps {
  data: any;
  className?: string;
  bgColor?: string;
}

function processParagraphs(paragraphs: any) {
  paragraphs.forEach((paragraph: any) => {
    if (isLinkOnly(paragraph)) {
      paragraph.children.forEach((child: any) => {
        if (child.type === "link" && child.children) {
          child.children.forEach((linkChild: any) => {
            if (linkChild.type === "text" && !linkChild.text.includes("-link-only")) {
              linkChild.text += "-link-only";
            }
          });
        }
      });
    }
  });
}

function isLinkOnly(paragraph: any) {
  if (paragraph.type !== "paragraph" || !paragraph.children || paragraph.children.length === 0) {
    return false;
  }

  // Track the presence of exactly one link and ensure other nodes are empty text
  let linkCount = 0;

  for (const child of paragraph.children) {
    if (child.type === "text" && child.text.trim() === "") {
      continue; // Ignore empty text nodes
    } else if (child.type === "link" && child.url) {
      linkCount += 1;
      if (linkCount > 1) return false; // More than one link found
    } else {
      return false; // Any other type means this is not a "link-only" paragraph
    }
  }

  return linkCount === 1; // Return true if exactly one link was found
}

function containsIframe(children: any[]) {
  return children.some((child: any) => {
    if (typeof child?.props?.text === "string" && child.props.text.trim().startsWith("<iframe")) {
      return true;
    }
    return false;
  });
}

function containsTwitterEmbed(children: any[]) {
  return children.some((child: any) => {
    if (
      typeof child?.props?.text === "string" &&
      (child.props.text.trim().startsWith('<blockquote class="twitter-tweet"') ||
        child.props.text.trim().includes('class="twitter-tweet"'))
    ) {
      return true;
    }
    return false;
  });
}

// Helper function to decode HTML entities
function decodeHtmlEntities(text: string): string {
  const entities: { [key: string]: string } = {
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
    '&quot;': '"',
    '&#39;': "'",
  };
  
  return text.replace(/&[#\w]+;/g, (entity) => entities[entity] || entity);
}

// Custom box parsing function
function parseCustomBox(text: string): { type: 'blue-box' | 'yellow-box' | 'white-box' | null; content: string } {
  // Decode HTML entities first
  const decodedText = decodeHtmlEntities(text);
  
  const boxPatterns = [
    { type: 'blue-box' as const, regex: /<blue-box>\s*([\s\S]*?)\s*<\/blue-box>/ },
    { type: 'yellow-box' as const, regex: /<yellow-box>\s*([\s\S]*?)\s*<\/yellow-box>/ },
    { type: 'white-box' as const, regex: /<white-box>\s*([\s\S]*?)\s*<\/white-box>/ },
  ];

  for (const pattern of boxPatterns) {
    const match = decodedText.match(pattern.regex);
    if (match) {
      return { type: pattern.type, content: match[1].trim() };
    }
  }

  return { type: null, content: text };
}

// Get styling classes for each box type using NSC brand colors
function getBoxStyling(boxType: 'blue-box' | 'yellow-box' | 'white-box'): string {
  const styles = {
    'blue-box': 'bg-nsc-ice-blue text-nsc-dark-blue',
    'yellow-box': 'bg-nsc-yellow text-nsc-dark-blue', 
    'white-box': 'bg-white border border-nsc-dark-blue text-nsc-dark-blue shadow-sm',
  };
  
  return styles[boxType];
}

// Pre-process data to collect multi-paragraph custom boxes into rich block groups
function mergeMultiLineBoxes(data: any[]): any[] {
  // Clear registry for fresh render
  boxContentRegistry.clear();
  boxIdCounter = 0;

  const boxOpenRegex = /<(blue-box|yellow-box|white-box)>/;
  const boxCloseRegex = /<\/(blue-box|yellow-box|white-box)>/;

  const result: any[] = [];
  let accumulating = false;
  let accumulatedBlocks: any[] = [];
  let currentBoxType: 'blue-box' | 'yellow-box' | 'white-box' = 'blue-box';

  for (const item of data) {
    const child = item.children?.[0];
    const isCodeText = item.type === 'paragraph'
      && item.children?.length === 1
      && child?.type === 'text'
      && child?.code === true;

    if (!accumulating) {
      if (isCodeText) {
        const decoded = decodeHtmlEntities(child.text);
        const openMatch = decoded.match(boxOpenRegex);
        const closeMatch = decoded.match(boxCloseRegex);

        if (openMatch && closeMatch) {
          // Single-line box — pass through as-is (handled by existing paragraph renderer)
          result.push(item);
        } else if (openMatch) {
          // Opening tag without closing — start collecting blocks
          accumulating = true;
          currentBoxType = openMatch[1] as typeof currentBoxType;
          accumulatedBlocks = [];

          // Extract text after the opening tag
          const afterTag = decoded.replace(boxOpenRegex, '').trim();
          if (afterTag) {
            accumulatedBlocks.push({
              type: 'paragraph',
              children: [{ type: 'text', text: afterTag }],
            });
          }
        } else {
          result.push(item);
        }
      } else {
        result.push(item);
      }
    } else {
      // We're accumulating blocks for a box
      // Check if this item contains the closing tag
      const closingChild = item.children?.[0];
      const isClosingCode = item.type === 'paragraph'
        && item.children?.length === 1
        && closingChild?.type === 'text'
        && closingChild?.code === true
        && boxCloseRegex.test(decodeHtmlEntities(closingChild.text));

      if (isClosingCode) {
        // Found closing tag — extract text before it
        const beforeTag = decodeHtmlEntities(closingChild.text).replace(boxCloseRegex, '').trim();
        if (beforeTag) {
          accumulatedBlocks.push({
            type: 'paragraph',
            children: [{ type: 'text', text: beforeTag }],
          });
        }

        // Store blocks in registry and emit marker
        const boxId = `__box_${boxIdCounter++}`;
        boxContentRegistry.set(boxId, {
          type: currentBoxType,
          blocks: accumulatedBlocks.map(stripCodeFlag),
        });

        result.push({
          type: 'paragraph',
          children: [{ type: 'text', code: true, text: `__BOX_MARKER__${boxId}` }],
        });

        accumulating = false;
        accumulatedBlocks = [];
      } else {
        // Regular block inside the box — collect it
        accumulatedBlocks.push(item);
      }
    }
  }

  // If unclosed tag, return data unmodified
  if (accumulating) {
    return data;
  }

  return result;
}


// Anchor parsing function
function parseAnchor(text: string): { isAnchor: boolean; id: string | null } {
  // Decode HTML entities first
  const decodedText = decodeHtmlEntities(text);
  
  // Match <anchor id="some-id" /> or <anchor id='some-id' />
  const anchorRegex = /<anchor\s+id=["']([^"']+)["']\s*\/>/;
  const match = decodedText.match(anchorRegex);
  
  if (match) {
    return { isAnchor: true, id: match[1] };
  }
  
  return { isAnchor: false, id: null };
}

// Module-level registry for multi-line box content
const boxContentRegistry = new Map<string, { type: 'blue-box' | 'yellow-box' | 'white-box'; blocks: any[] }>();
let boxIdCounter = 0;

// Recursively strip `code: true` from text nodes (preserves bold, italic, etc.)
function stripCodeFlag(block: any): any {
  if (!block) return block;
  const result = { ...block };
  if (result.children) {
    result.children = result.children.map((child: any) => {
      if (child.type === 'text' && child.code === true) {
        const { code, ...rest } = child;
        return rest;
      }
      if (child.children) {
        return stripCodeFlag(child);
      }
      return child;
    });
  }
  return result;
}

// Block handlers for content rendered inside custom boxes (no box detection, no anchor logic)
function getInnerBoxBlockHandlers() {
  return {
    paragraph: ({ children }: any) => <p>{children}</p>,
    heading: ({ children, level }: any) => {
      const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;
      const headingClasses: Record<number, string> = {
        1: "text-2xl font-bold mb-4 mt-6",
        2: "text-xl font-bold mb-3 mt-5 uppercase",
        3: "text-lg font-bold mb-2 mt-4",
        4: "text-base font-bold mb-2 mt-3",
        5: "text-sm font-bold mb-1 mt-2",
        6: "text-sm font-bold mb-1 mt-2",
      };
      return (
        <HeadingTag className={headingClasses[level] || headingClasses[1]}>
          {children}
        </HeadingTag>
      );
    },
    list: ({ children, format }: any) => {
      const ListTag = format === "ordered" ? "ol" : "ul";
      return (
        <ListTag className="list-none pl-7">
          {children}
        </ListTag>
      );
    },
    "list-item": ({ children }: any) => (
      <li className="relative mt-2 first:mt-0 font-roboto font-light text-lg leading-[30px]">
        {children}
      </li>
    ),
    image: ({ image }: any) => (
      <img
        className="rounded"
        src={image.url.replace("localhost", "127.0.0.1")}
        alt={image.alternativeText || ""}
      />
    ),
    link: ({ children, url }: any) => {
      const external = url.startsWith("http");
      return external ? (
        <a href={url} target="_blank" rel="noopener noreferrer">{children}</a>
      ) : (
        <Link href={url}>{children}</Link>
      );
    },
  };
}

export default function RichText({ data, className = "rich-text relative", bgColor }: RichTextProps) {
  const mergedData = mergeMultiLineBoxes(data);
  processParagraphs(mergedData);

  // Auto-scroll to anchor on page load and hash changes
  useEffect(() => {
    const scrollToAnchor = () => {
      // Check if there's a hash in the URL
      const hash = window.location.hash;
      if (hash) {
        // Remove the '#' and find the element
        const id = hash.substring(1);
        const element = document.getElementById(id);
        
        if (element) {
          // Small delay to ensure the page has fully rendered
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      }
    };

    // Scroll on initial load
    scrollToAnchor();

    // Listen for hash changes (when navigating with anchor links)
    window.addEventListener('hashchange', scrollToAnchor);

    // Cleanup
    return () => {
      window.removeEventListener('hashchange', scrollToAnchor);
    };
  }, []);

  return (
    <section className={cn(className, getTextColorByBgColor(bgColor) === "#ffffff" && "text-white")}>
      <BlocksRenderer
        content={mergedData}
        blocks={{
          paragraph: ({ children }: any) => {
            if (containsIframe(children)) {
              return (
                <div
                  className="iframe-container w-full"
                  dangerouslySetInnerHTML={{ __html: children[0]?.props?.text }}
                />
              );
            }

            if (containsTwitterEmbed(children)) {
              return (
                <div className="twitter-embed-container w-full">
                  <div dangerouslySetInnerHTML={{ __html: children[0]?.props?.text }} />
                  <script async src="https://platform.twitter.com/widgets.js" charSet="utf-8"></script>
                </div>
              );
            }

            // Check if paragraph contains only a custom box, anchor, or box marker
            if (children && children.length === 1 && children[0]?.props?.code === true) {
              const text = children[0]?.props?.text;
              if (typeof text === 'string') {
                // Check for multi-line box marker
                if (text.startsWith('__BOX_MARKER__')) {
                  const boxId = text.replace('__BOX_MARKER__', '');
                  const boxData = boxContentRegistry.get(boxId);
                  if (boxData) {
                    return (
                      <div className={cn("my-4 p-4 rounded-lg", getBoxStyling(boxData.type))}>
                        <BlocksRenderer
                          content={boxData.blocks}
                          blocks={getInnerBoxBlockHandlers()}
                        />
                      </div>
                    );
                  }
                }

                // Check for anchor
                const anchorParsed = parseAnchor(text);
                if (anchorParsed.isAnchor && anchorParsed.id) {
                  return <div id={anchorParsed.id} className="scroll-mt-20" />;
                }

                // Then check for single-line custom boxes
                const parsed = parseCustomBox(text);
                if (parsed.type) {
                  return (
                    <div className={cn(
                      "my-4 p-4 rounded-lg",
                      getBoxStyling(parsed.type)
                    )}>
                      <div className="whitespace-pre-wrap">{parsed.content}</div>
                    </div>
                  );
                }
              }
            }

            // Process children to handle inline anchor tags
            let hasAnchor = false;
            let anchorId = null;
            
            // Filter out anchor elements and extract the ID
            const processedChildren = children?.map((child: any, index: number) => {
              if (child?.props?.code === true && typeof child?.props?.text === 'string') {
                const anchorParsed = parseAnchor(child.props.text);
                if (anchorParsed.isAnchor && anchorParsed.id) {
                  hasAnchor = true;
                  anchorId = anchorParsed.id;
                  return null; // Remove the anchor from visible content
                }
              }
              return child;
            }).filter(Boolean); // Remove null entries

            // If we found an anchor, render it as an invisible div before the paragraph
            if (hasAnchor && anchorId) {
              return (
                <>
                  <div id={anchorId} className="scroll-mt-40" />
                  <p>{processedChildren}</p>
                </>
              );
            }

            return <p>{children}</p>;
          },
          // Header blocks
          heading: ({ children, level }: any) => {
            const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;
            const headingClasses = {
              1: "text-2xl font-bold mb-4 mt-6",
              2: "text-xl font-bold mb-3 mt-5 uppercase",
              3: "text-lg font-bold mb-2 mt-4",
              4: "text-base font-bold mb-2 mt-3",
              5: "text-sm font-bold mb-1 mt-2",
              6: "text-sm font-bold mb-1 mt-2"
            };
            
            return (
              <HeadingTag className={headingClasses[level as keyof typeof headingClasses] || headingClasses[1]}>
                {children}
              </HeadingTag>
            );
          },
          // List blocks. `list-none` is intentionally omitted so callers that
          // render RichText without the `.rich-text` class still get a native
          // marker; under `.rich-text`, its CSS re-applies list-none + the
          // styled `::before` counter/dot (see globals.scss).
          list: ({ children, format }: any) => {
            const ListTag = format === "ordered" ? "ol" : "ul";
            return <ListTag className="pl-7">{children}</ListTag>;
          },
          "list-item": ({ children }: any) => {
            return <RichTextListItem>{children}</RichTextListItem>;
          },
          image: ({ image }) => {
            return (
              <img
                className="rounded"
                src={image.url.replace("localhost", "127.0.0.1")}
                alt={image.alternativeText || ""}
              />
            );
          },
          link: ({ children, url }) => {
            const external = url.startsWith("http");
            const childText = Array.isArray(children) && children[0]?.props?.text;
            if (childText?.includes("-link-only") && Array.isArray(children)) {
              const newChildren = children.map((child) => {
                return {
                  ...child,
                  props: {
                    text: childText.replace("-link-only", ""),
                  },
                };
              });

              return external ? (
                <a href={url} target="_blank" className="cta-link group" rel="noopener noreferrer ">
                  {newChildren}{" "}
                  <Arrow
                    color={getTextColorByBgColor(bgColor)}
                    className="ml-3 block min-w-fit transition-transform group-hover:translate-x-1"
                  />
                </a>
              ) : (
                <Link className="cta-link group flex" href={url}>
                  {newChildren}{" "}
                  <Arrow
                    color={getTextColorByBgColor(bgColor)}
                    className="ml-3 inline-block min-w-fit transition-transform group-hover:translate-x-1"
                  />
                </Link>
              );
            }

            return external ? (
              <a href={url} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ) : (
              <Link href={url}>{children}</Link>
            );
          },
          // Custom code block handler for text boxes and anchors
          code: ({ children }: any) => {
            const text = Array.isArray(children) && children[0]?.props?.text;
            if (typeof text === 'string') {
              // Check for anchor first
              const anchorParsed = parseAnchor(text);
              if (anchorParsed.isAnchor && anchorParsed.id) {
                return <div id={anchorParsed.id} className="scroll-mt-20" />;
              }
              
              // Then check for custom boxes
              const parsed = parseCustomBox(text);
              if (parsed.type) {
                return (
                  <div className={cn(
                    "my-4 p-4 rounded-lg",
                    getBoxStyling(parsed.type)
                  )}>
                    <div className="whitespace-pre-wrap">{parsed.content}</div>
                  </div>
                );
              }
            }
            
            // Default code block rendering if no custom tags found
            return (
              <pre className="bg-gray-100 p-4 rounded-lg my-4 overflow-x-auto">
                <code className="text-sm">{children}</code>
              </pre>
            );
          },
        }}
      />
    </section>
  );
}
